import React, { useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  margin-left: 16px;
  
  &:hover {
    color: white;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const ReasonsSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.label`
  display: block;
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ReasonOption = styled.label`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &.selected {
    background: rgba(102, 126, 234, 0.1);
    border-color: #667eea;
  }
`;

const RadioInput = styled.input`
  margin-right: 12px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ReasonText = styled.span`
  color: white;
  font-size: 0.95rem;
  flex: 1;
`;

const OtherReasonInput = styled.input`
  width: 100%;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.95rem;
  margin-top: 8px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const CommentsSection = styled.div`
  margin-bottom: 24px;
`;

const CommentsTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.95rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #dc3545, #e74c3c);
    color: white;
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #c82333, #dc2626);
      transform: translateY(-2px);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, otherReason?: string, comments?: string) => Promise<void>;
}

const CANCELLATION_REASONS = [
  { value: 'budget_constraints', label: 'Budget constraints' },
  { value: 'lack_of_value', label: 'Lack of value' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'lack_of_use', label: 'Lack of use' },
  { value: 'missing_features', label: 'Missing features' },
  { value: 'unmet_expectations', label: 'Unmet expectations' },
  { value: 'using_alternative', label: 'Using an alternative product' },
  { value: 'other', label: 'Other' }
];

export const CancellationModal: React.FC<CancellationModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm 
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState('');
  const [comments, setComments] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!selectedReason) {
      setError('Please select a reason for cancellation');
      return;
    }

    if (selectedReason === 'other' && !otherReason.trim()) {
      setError('Please provide a reason');
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      await onConfirm(
        selectedReason,
        selectedReason === 'other' ? otherReason : undefined,
        comments || undefined
      );
      
      // Reset form after successful submission
      setSelectedReason('');
      setOtherReason('');
      setComments('');
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedReason('');
      setOtherReason('');
      setComments('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Cancel Your Subscription</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isProcessing}>×</CloseButton>
        </ModalHeader>

        <Description>
          We're sorry to see you go. Please let us know why you're canceling so we can improve our service.
        </Description>

        <ReasonsSection>
          <SectionLabel>Why are you canceling? *</SectionLabel>
          {CANCELLATION_REASONS.map(reason => (
            <ReasonOption 
              key={reason.value}
              className={selectedReason === reason.value ? 'selected' : ''}
            >
              <RadioInput
                type="radio"
                name="cancellationReason"
                value={reason.value}
                checked={selectedReason === reason.value}
                onChange={(e) => setSelectedReason(e.target.value)}
                disabled={isProcessing}
              />
              <ReasonText>{reason.label}</ReasonText>
            </ReasonOption>
          ))}
          
          {selectedReason === 'other' && (
            <OtherReasonInput
              type="text"
              placeholder="Please specify your reason..."
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              disabled={isProcessing}
              maxLength={500}
            />
          )}
        </ReasonsSection>

        <CommentsSection>
          <SectionLabel>Additional comments (optional)</SectionLabel>
          <CommentsTextarea
            placeholder="Is there anything else you'd like us to know?"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isProcessing}
            maxLength={1000}
          />
        </CommentsSection>

        {error && (
          <div style={{ 
            color: '#ef4444', 
            fontSize: '0.9rem', 
            marginBottom: '16px',
            padding: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px'
          }}>
            {error}
          </div>
        )}

        <ButtonContainer>
          <Button 
            $variant="secondary" 
            onClick={handleClose}
            disabled={isProcessing}
          >
            Keep Subscription
          </Button>
          <Button 
            $variant="primary" 
            onClick={handleConfirm}
            disabled={isProcessing || !selectedReason}
          >
            {isProcessing && <LoadingSpinner />}
            {isProcessing ? 'Canceling...' : 'Confirm Cancellation'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};